"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const fases = [{
  numero: "01",
  nombre: "Entender",
  titulo: "Mapear la carga.",
  descripcion: "Antes de proponer cualquier cosa, entendemos qué está pasando. Sesiones con stakeholders clave, mapeo de procesos críticos, inventario del stack tecnológico actual, identificación de dolores reales — los que el equipo vive todos los días, no los que aparecen en presentaciones.",
  pregunta: "¿Qué está cargando el negocio hoy y dónde duele?"
}, {
  numero: "02",
  nombre: "Trazar",
  titulo: "Encontrar y anclar el fulcro.",
  descripcion: "Con la carga mapeada, identificamos el punto de apoyo. No el problema más visible — el que tiene mayor efecto palanca. Diseñamos la arquitectura recomendada y un roadmap priorizado por valor de negocio, no por orden de queja.",
  pregunta: "¿Dónde, exactamente, una inversión técnica multiplica el resultado?"
}, {
  numero: "03",
  nombre: "Construir",
  titulo: "Aplicar la fuerza.",
  descripcion: "Implementación técnica. Workflows en producción, integraciones que conectan los sistemas que ya usas, OCR e IA cuando aplican, reportes que se actualizan solos. Cada componente entregado viene con documentación técnica generada automáticamente — cero caja negra.",
  pregunta: "¿El componente funciona y queda documentado para que cualquier ingeniero lo entienda?"
}, {
  numero: "04",
  nombre: "Operar",
  titulo: "Mantener el movimiento.",
  descripcion: "Lo construido necesita seguir vivo. Monitoreo continuo, ajustes menores, correcciones, soporte. La infraestructura sigue funcionando sin que el cliente tenga que pensar en ella todos los días.",
  pregunta: "¿Qué sigue corriendo bien y qué empezó a fallar?"
}, {
  numero: "05",
  nombre: "Evolucionar",
  titulo: "Recalibrar el punto.",
  descripcion: "Los negocios cambian. La arquitectura que tenía sentido hace dieciocho meses puede estar pidiendo otra cosa. Revisamos lo construido contra el estado actual del negocio, identificamos qué dejó de aplicar y qué frente nuevo abrir.",
  pregunta: "¿El fulcro sigue en el punto correcto, o el negocio movió la carga?"
}];
export default function LasCincoFases() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="234143" id="las-5-fases" className="bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        <div ref={headerRef} className={`max-w-3xl mb-20 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-normal text-foreground leading-tight mb-6">
            Cinco fases. Una palanca.
          </h2>
          <p className="font-sans text-lg text-muted-foreground leading-relaxed">
            El método baja la filosofía a práctica. Cada fase responde a una pregunta concreta del negocio y produce un entregable verificable.
          </p>
        </div>

        <div className="space-y-20 md:space-y-28">
          {fases.map((fase, i) => <FaseBloque key={fase.numero} fase={fase} index={i} />)}
        </div>
      </div>
    </section>;
}
function FaseBloque({
  fase,
  index
}: {
  fase: {
    numero: string;
    nombre: string;
    titulo: string;
    descripcion: string;
    pregunta: string;
  };
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`grid grid-cols-1 md:grid-cols-12 gap-y-8 md:gap-x-12 items-start transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="md:col-span-4">
        <div className="font-mono text-sm tracking-widest text-primary mb-3">
          FASE {fase.numero}
        </div>
        <h3 className="font-serif text-5xl md:text-6xl font-normal text-foreground leading-[0.95] break-words">
          {fase.nombre}
        </h3>
      </div>

      <div className="md:col-span-8 md:pt-2 space-y-6">
        <p className="font-serif italic text-2xl md:text-3xl text-foreground leading-snug">
          {fase.titulo}
        </p>

        <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed">
          {fase.descripcion}
        </p>

        <div className="border-l-2 border-primary pl-6 mt-2">
          <div className="font-mono text-xs tracking-widest text-primary uppercase mb-2">
            La pregunta que se contesta
          </div>
          <p className="font-serif italic text-lg md:text-xl text-foreground leading-snug">
            {fase.pregunta}
          </p>
        </div>
      </div>
    </div>;
}